/**
 * What the Smiths remember, for the person looking.
 *
 * Two kinds of thing, and the difference is the whole screen. **Yours** are the
 * ones only you see and only you decide about — those come first, with whatever
 * is waiting on you at the top, because that is the part of this page that is
 * actually an ask. **Your company's** are shared with everybody there and are
 * read-only here; an administrator governs those, and this page says so rather
 * than offering buttons that would be refused.
 *
 * Nothing on this screen names who taught a memory. The service does not send
 * it, and it should not: one colleague reading what another typed into a Smith
 * is the thing that would make people stop typing.
 *
 * A person writes one of their own as a sentence, with optional details; it is
 * sent as typed and is in use, for them alone, as soon as it is saved.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { PortalMemory, PortalMemoryGroup, PortalMemoryKindChoice, PortalMemoryLimits } from '@portal/contracts'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  discardMemory,
  getMemories,
  rewordMemory,
  startUsingMemory,
  stopUsingMemory,
  writeMemory
} from '../api/memories'
import { PortalApiError } from '../api/portal-client'
import { useSession } from '../auth/useSession'
import { memoriesKey } from '../hooks/queryClient'
import { describeApiError, useI18n } from '../i18n'
import type { Messages } from '../i18n/messages'

/**
 * The label for what kind of thing a memory is, or nothing at all.
 *
 * Exported for `MemoriesPage.test.ts`. A class this build has no word for
 * renders with NO label rather than with its raw value: the sentence is what a
 * customer reads, and a token like `learned_pattern` beside it is noise at best
 * and a peek at our internals at worst.
 */
export function memoryKindLabel(kind: string, kinds: Record<string, string>): string | undefined {
  return kinds[kind]
}

/**
 * The key a memory's row is mounted under. A row keeps what its person is typing (the reword draft, the reason) in its
 * own state, started from the memory's words once; keying it on the version remounts it when a refetch brings a new
 * one, so an open form starts again from what the memory now says instead of from words that were replaced, while a
 * refetch that changes nothing leaves the draft alone.
 *
 * Exported for `MemoriesPage.test.ts`.
 */
export function memoryRowKey(memory: Pick<PortalMemory, 'id' | 'version'>): string {
  return `${memory.id}:${memory.version}`
}

/**
 * Whether a refusal means the list on screen no longer matches the service: the memory changed, closed or went away,
 * or the Smith stopped keeping memories. The page then reads the list again, so the row comes back with what the
 * memory now says (under a new key, `memoryRowKey`) instead of refusing every save from the version it was read at.
 *
 * Exported for `MemoriesPage.test.ts`.
 */
export function listChangedUnder(err: unknown): boolean {
  const code = err instanceof PortalApiError ? err.issue?.code : undefined
  return code === 'memory_conflict' || code === 'memory_not_found' || code === 'memory_off'
}

/** What stands between a person's words and saving them, said before the request. */
export type MemoryWordsProblem = 'tooLong' | 'titleTooLong' | 'needsTitle'

/** A title as typed, on one line as the service stores it; blank is none. */
function typedTitle(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim()
}

/**
 * The refusal the service would make, or `null`: no sentence, a sentence over its limit, or details over theirs. The
 * sentence is the whole memory and the details are optional, so empty details are never a problem. The limits come
 * with the memories; without them only the missing sentence is caught here, and the service says the rest.
 */
export function memoryWordsProblem(
  text: string,
  title: string,
  limits?: PortalMemoryLimits
): MemoryWordsProblem | null {
  const head = typedTitle(title)
  if (head === '') return 'needsTitle'
  if (!limits) return null
  if (head.length > limits.titleMaxChars) return 'titleTooLong'
  if (text.trim().length > limits.bodyMaxChars) return 'tooLong'
  return null
}

/**
 * How close a field's length is to its limit, for the count beside the field: `near` from nine tenths of the
 * limit, so the number is read before the refusal, and `over` past it.
 */
export function characterCountTone(used: number, max: number): 'ok' | 'near' | 'over' {
  if (used > max) return 'over'
  if (used >= Math.ceil(max * 0.9)) return 'near'
  return 'ok'
}

/**
 * The length of what is typed against the limit, on its own beside the hint. It changes on every key, and written
 * at the end of the hint it read as part of the explanation.
 */
function CharacterCount({
  id,
  used,
  max,
  strings
}: {
  id: string
  used: number
  max: number
  strings: Messages['memories']
}) {
  const tone = characterCountTone(used, max)
  return (
    <span id={id} className={tone === 'ok' ? 'mem-count' : `mem-count is-${tone}`}>
      {strings.count(used, max)}
    </span>
  )
}

/** The sentence a problem shows, in the person's language, with the limit it is about. */
export function memoryWordsProblemText(
  problem: MemoryWordsProblem,
  limits: PortalMemoryLimits,
  strings: Messages['memories']
): string {
  if (problem === 'tooLong') return strings.tooLong(limits.bodyMaxChars)
  if (problem === 'titleTooLong') return strings.titleTooLong(limits.titleMaxChars)
  return strings.needsTitle
}

/**
 * A long memory folded to the length the Smith is first shown of it, or `null` when it is short enough to show whole
 * (or the limits did not come). Characters are counted as the service counts every limit on a memory, in UTF-16 units,
 * so an emoji counts as two; a cut that would fall between the two halves of one character stops before it.
 */
export function foldedMemoryText(text: string, limits?: PortalMemoryLimits): string | null {
  if (!limits) return null
  const body = text.trim()
  const at = limits.bodyInlineMaxChars
  if (body.length <= at) return null
  const last = body.charCodeAt(at - 1)
  const end = at > 0 && last >= 0xd800 && last <= 0xdbff ? at - 1 : at
  return `${body.slice(0, end).trimEnd()}…`
}

/**
 * The sentence a reword sends: `undefined` when it did not change, so the one written stays, or the new one. Never one
 * derived from the details — the service sends only the one somebody wrote, and so does this.
 */
export function titleToSend(memory: Pick<PortalMemory, 'title'>, typed: string): string | undefined {
  const next = typedTitle(typed)
  return next === typedTitle(memory.title ?? '') ? undefined : next
}

/**
 * The order the Smiths appear in.
 *
 * Whatever is waiting on this person first, most-waiting first, then everything
 * else by name. Exported for `MemoriesPage.test.ts` — the page is otherwise
 * just this order rendered.
 */
/** Whether the page has anything to show, and if not, whether the filter is why. */
export type MemoriesEmptyState = 'no-smith' | 'none' | 'none-here' | 'has-memories'

/**
 * Which of the two empty messages this page owes, if either.
 *
 * The distinction is the whole of it. The page can be opened filtered to one
 * Smith — the waiting card on the home page links that way — and a filtered list
 * that holds nothing says nothing about the other Smiths. Since the selector
 * shows which Smith is on screen, the filtered message is kept for the one case
 * no section can explain: a link naming a Smith this person does not see. Deciding from the
 * filtered list alone made a person whose other Smiths remember plenty read that
 * nothing had ever been remembered, and it did so most readily for a link naming
 * a Smith that no longer matches anything: `every` over an empty list is true, so
 * the strongest possible claim came from the least possible evidence.
 */
export function memoriesEmptyState(args: {
  all: ReadonlyArray<PortalMemoryGroup>
  shown: ReadonlyArray<PortalMemoryGroup>
  filtered: boolean
}): MemoriesEmptyState {
  // A Smith whose memories could not be read holds no evidence either way, so it
  // cannot be counted toward "nothing here". Its own notice already says what
  // happened, and adding an outage to a claim about what has been remembered is
  // the invention the home page is careful not to make with its waiting count.
  // No Smith keeps memories for this company: the service leaves out every Smith whose memory is off. "Nothing has
  // been remembered yet" would promise that working will fill the page, and nothing will until an operator switches
  // memory on.
  if (args.all.length === 0) return 'no-smith'
  const readable = (g: PortalMemoryGroup): boolean => g.unavailable !== true
  const bare = (g: PortalMemoryGroup): boolean => g.shared.length === 0 && g.personal.length === 0
  const saysNothing = (gs: ReadonlyArray<PortalMemoryGroup>): boolean => gs.every(readable) && gs.every(bare)
  if (saysNothing(args.all)) return 'none'
  // A Smith the person can see renders its own section, which already says it
  // holds nothing, and the selector above it says which Smith is shown and what
  // waits elsewhere. The page speaks only when the choice matched no Smith at all.
  if (args.filtered && args.shown.length === 0) return 'none-here'
  return 'has-memories'
}

/**
 * Whether a Smith's list was cut at the service's ceiling, so the page says it shows only the most recent memories
 * rather than letting a short list pass for the whole. A Smith that could not be read says that instead.
 */
export function showsListCut(group: Pick<PortalMemoryGroup, 'truncated' | 'unavailable'>): boolean {
  return group.unavailable !== true && group.truncated
}

export function groupsForDisplay(groups: PortalMemoryGroup[]): PortalMemoryGroup[] {
  return [...groups].sort(
    (a, b) => b.waiting - a.waiting || a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
  )
}

/**
 * The Smith a link or the selector asked for, or none.
 *
 * An empty `?flowId=` is no choice at all. Read as a Smith named `''` it would
 * match nothing and claim this person's Smiths hold nothing, which is the claim
 * `memoriesEmptyState` exists to keep the page from making on no evidence.
 */
export function focusFromParam(value: string | null): string | null {
  return value ? value : null
}

export interface SmithFilter {
  /** Whether to offer the choice at all: with one Smith there is nothing to choose. */
  show: boolean
  allLabel: string
  options: ReadonlyArray<{ flowId: string; label: string }>
}

/**
 * The selector that narrows the page to one Smith.
 *
 * Its options come from the same answer the page renders, so they cost no request
 * and always agree with the sections below. They are in name order, where a person
 * looks for a Smith they already know, while the sections keep putting what waits
 * first; and each carries its own waiting count, so narrowing to one Smith never
 * hides where the others need an answer. A Smith a link names and this person does
 * not see stays selectable under its id, as the history filter does, so the choice
 * the page is showing is always the one the control shows.
 */
export function smithFilter(
  all: ReadonlyArray<PortalMemoryGroup>,
  focus: string | null,
  strings: Pick<Messages['memories'], 'allSmiths' | 'waitingCount'>
): SmithFilter {
  const withCount = (name: string, waiting: number): string =>
    waiting > 0 ? `${name} · ${strings.waitingCount(waiting)}` : name
  const options = [...all]
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }))
    .map((g) => ({ flowId: g.flowId, label: withCount(g.displayName, g.waiting) }))
  if (focus !== null && !all.some((g) => g.flowId === focus)) options.push({ flowId: focus, label: focus })
  const total = all.reduce((sum, g) => sum + g.waiting, 0)
  return { show: all.length > 1, allLabel: withCount(strings.allSmiths, total), options }
}

/**
 * What a person can do to one of their own memories, and whether it waits for a second click.
 *
 * Using a waiting memory acts at once: it is the answer the row is asking for, and using one can be stopped later.
 * Everything else opens a step first — rewording and discarding because they need words, and stopping because it
 * cannot be undone, which the person must read before it happens rather than after.
 */
export type RowAct = 'use' | 'reword' | 'discard' | 'stop'
export function rowActs(memory: { inForce: boolean }): RowAct[] {
  return memory.inForce ? ['reword', 'stop'] : ['use', 'reword', 'discard']
}
export function actsAtOnce(act: RowAct): act is 'use' {
  return act === 'use'
}

type AskedAct = Exclude<RowAct, 'use'>
type Pending = { id: string; kind: AskedAct } | null

/** The order the kinds are offered in, commonest first. */
const WRITE_KINDS: PortalMemoryKindChoice[] = ['preference', 'prohibition', 'fact']

export function MemoriesPage() {
  const { t, formatDate } = useI18n()
  const { contractor } = useSession()
  const [params, setParams] = useSearchParams()
  const focusFlow = focusFromParam(params.get('flowId'))
  const memories = useQuery({ queryKey: memoriesKey, queryFn: getMemories, staleTime: 0 })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [writingFor, setWritingFor] = useState<string | null>(null)

  const run = async (id: string, action: () => Promise<unknown>) => {
    setBusyId(id)
    setError(null)
    try {
      await action()
      setPending(null)
      await memories.refetch()
    } catch (err) {
      setError(err instanceof PortalApiError ? describeApiError(t, err) : t.common.genericError)
      if (listChangedUnder(err)) void memories.refetch()
    } finally {
      setBusyId(null)
    }
  }

  // Narrowing to another Smith closes whatever was open on the one left behind: a
  // draft or a confirmation for a Smith no longer on screen would come back later
  // holding a question nobody is looking at.
  const chooseSmith = (flowId: string) => {
    const next = new URLSearchParams(params)
    if (flowId) next.set('flowId', flowId)
    else next.delete('flowId')
    setParams(next, { replace: true })
    setWritingFor(null)
    setPending(null)
    setError(null)
  }

  const all = memories.data ? groupsForDisplay(memories.data.groups) : []
  const groups = focusFlow ? all.filter((g) => g.flowId === focusFlow) : all
  const emptiness = memories.data ? memoriesEmptyState({ all, shown: groups, filtered: focusFlow !== null }) : null
  const filter = smithFilter(all, focusFlow, t.memories)
  const limits = memories.data?.limits

  return (
    <div className="page portal-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t.memories.title}</h1>
          <div className="page-sub">{t.memories.subtitle}</div>
        </div>
      </header>

      {filter.show && (
        <div className="history-filters mem-filters">
          <select
            className="select"
            aria-label={t.memories.filterBySmith}
            value={focusFlow ?? ''}
            onChange={(e) => chooseSmith(e.target.value)}
          >
            <option value="">{filter.allLabel}</option>
            {filter.options.map((o) => (
              <option key={o.flowId} value={o.flowId}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {memories.isLoading && (
        <div className="portal-loading">
          <Spinner size={18} /> {t.memories.loading}
        </div>
      )}

      {memories.isError && <Notice kind="danger">{describeApiError(t, memories.error)}</Notice>}
      {error && <Notice kind="danger">{error}</Notice>}

      {emptiness === 'no-smith' && <Notice kind="info">{t.memories.noSmithKeeping}</Notice>}
      {emptiness === 'none' && <Notice kind="info">{t.memories.empty}</Notice>}
      {emptiness === 'none-here' && (
        <Notice kind="info">
          {t.memories.emptyHere} <Link to="/memories">{t.memories.showAll}</Link>
        </Notice>
      )}

      {groups.map((group) => (
        <section className="mem-group" key={group.flowId}>
          <div className="mem-group-head">
            <h2 className="mem-group-title">{group.displayName}</h2>
            {group.waiting > 0 && (
              <Badge kind="waiting" dot>
                {t.memories.waitingCount(group.waiting)}
              </Badge>
            )}
          </div>

          {group.unavailable && <Notice kind="warning">{t.memories.unavailable}</Notice>}
          {showsListCut(group) && <Notice kind="info">{t.memories.listCut}</Notice>}
          {/* How full this Smith's memory is, said only when it matters: before the
            person tries to use or add a memory and is refused. */}
          {!group.unavailable && group.capacity === 'near' && <Notice kind="info">{t.memories.capacityNear}</Notice>}
          {!group.unavailable && group.capacity === 'full' && <Notice kind="warning">{t.memories.capacityFull}</Notice>}

          {!group.unavailable && (
            <WriteMemory
              flowId={group.flowId}
              strings={t.memories}
              limits={limits}
              open={writingFor === group.flowId}
              busy={busyId === `write:${group.flowId}`}
              onOpen={() => {
                setError(null)
                setWritingFor(writingFor === group.flowId ? null : group.flowId)
              }}
              onSave={(text, kind, title) =>
                run(`write:${group.flowId}`, async () => {
                  await writeMemory(group.flowId, text, kind, title)
                  setWritingFor(null)
                })
              }
            />
          )}

          {!group.unavailable && group.personal.length === 0 && group.shared.length === 0 && (
            <div className="mem-empty muted">{t.memories.noneForSmith}</div>
          )}

          {group.personal.length > 0 && (
            <div className="mem-block">
              <div className="mem-block-head">
                <span className="mem-block-title">{t.memories.yours}</span>
                <span className="muted">{t.memories.yoursSub}</span>
              </div>
              <ul className="mem-list">
                {group.personal.map((memory) => (
                  <MemoryRow
                    key={memoryRowKey(memory)}
                    memory={memory}
                    strings={t.memories}
                    limits={limits}
                    savedOn={t.memories.savedOn(formatDate(memory.savedAt))}
                    busy={busyId === memory.id}
                    pending={pending?.id === memory.id ? pending.kind : null}
                    onAsk={(kind) => {
                      setError(null)
                      setPending(kind ? { id: memory.id, kind } : null)
                    }}
                    onUse={() => run(memory.id, () => startUsingMemory(memory.id, group.flowId, memory.version))}
                    onStopUsing={() => run(memory.id, () => stopUsingMemory(memory.id, group.flowId, memory.version))}
                    onDiscard={(reason) =>
                      run(memory.id, () => discardMemory(memory.id, group.flowId, memory.version, reason))
                    }
                    onReword={(text, title) =>
                      run(memory.id, () => rewordMemory(memory.id, group.flowId, memory.version, text, title))
                    }
                  />
                ))}
              </ul>
            </div>
          )}

          {group.shared.length > 0 && (
            <div className="mem-block">
              <div className="mem-block-head">
                <span className="mem-block-title">{t.memories.company}</span>
                <span className="muted">{t.memories.companySub(contractor?.name ?? '')}</span>
              </div>
              <ul className="mem-list">
                {group.shared.map((memory) => (
                  <li className="mem-row is-shared" key={memory.id}>
                    <MemoryText memory={memory} limits={limits} strings={t.memories} />
                    <div className="mem-row-meta">
                      <KindChip kind={memory.kind} kinds={t.memories.kinds} />
                      <span className="muted">{t.memories.savedOn(formatDate(memory.savedAt))}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      ))}
    </div>
  )
}

function KindChip({ kind, kinds }: { kind: string; kinds: Record<string, string> }) {
  const label = memoryKindLabel(kind, kinds)
  if (!label) return null
  return <span className="chip">{label}</span>
}

/**
 * A memory as a person reads it: the sentence somebody wrote, and its details when there are any — folded, when they
 * are long, at the length the Smith is first shown of them, with the rest one click away. Line breaks are kept: two
 * thousand characters is paragraphs and lists.
 */
function MemoryText({
  memory,
  limits,
  strings
}: {
  memory: PortalMemory
  limits: PortalMemoryLimits | undefined
  strings: Messages['memories']
}) {
  const [open, setOpen] = useState(false)
  const folded = foldedMemoryText(memory.text, limits)
  return (
    <>
      {memory.title && <div className="mem-row-title">{memory.title}</div>}
      {memory.text.trim() && <div className="mem-row-text">{folded && !open ? folded : memory.text.trim()}</div>}
      {folded && (
        <button type="button" className="mem-row-fold" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? strings.showLess : strings.showWhole(memory.text.trim().length)}
        </button>
      )}
    </>
  )
}

/**
 * The sentence and the details, as both forms ask for them, with the counts and the problem said before saving.
 *
 * The sentence comes first and is the whole memory; the details are optional and are never the rest of it, since the
 * Smith reads the sentence first and alone when the details are long. The length details are shown whole to is said
 * only under the details, never next to the sentence's own limit, so the two numbers cannot be read as one. A form
 * with nothing typed yet shows no problem.
 */
function MemoryWordsFields({
  idPrefix,
  title,
  onTitle,
  text,
  onText,
  placeholder,
  limits,
  strings
}: {
  idPrefix: string
  title: string
  onTitle: (value: string) => void
  text: string
  onText: (value: string) => void
  placeholder?: string
  limits: PortalMemoryLimits | undefined
  strings: Messages['memories']
}) {
  const typed = title.trim() !== '' || text.trim() !== ''
  const problem = typed ? memoryWordsProblem(text, title, limits) : null
  return (
    <>
      <label className="field-label" htmlFor={`${idPrefix}-title`}>
        {strings.titleLabel}
      </label>
      <input
        id={`${idPrefix}-title`}
        className="input"
        value={title}
        placeholder={placeholder}
        maxLength={limits?.titleMaxChars}
        aria-describedby={limits ? `${idPrefix}-title-count` : undefined}
        onChange={(e) => onTitle(e.target.value)}
      />
      <div className="mem-field-foot">
        <div className="mem-row-hint muted">{strings.titleHint}</div>
        {limits && (
          <CharacterCount
            id={`${idPrefix}-title-count`}
            used={typedTitle(title).length}
            max={limits.titleMaxChars}
            strings={strings}
          />
        )}
      </div>
      <label className="field-label" htmlFor={`${idPrefix}-text`}>
        {strings.detailsLabel}
      </label>
      <textarea
        id={`${idPrefix}-text`}
        className="textarea"
        rows={4}
        value={text}
        maxLength={limits?.bodyMaxChars}
        aria-describedby={limits ? `${idPrefix}-text-count` : undefined}
        onChange={(e) => onText(e.target.value)}
      />
      {limits && (
        <div className="mem-field-foot">
          <div className="mem-row-hint muted">{strings.detailsHint(limits.bodyInlineMaxChars)}</div>
          <CharacterCount
            id={`${idPrefix}-text-count`}
            used={text.trim().length}
            max={limits.bodyMaxChars}
            strings={strings}
          />
        </div>
      )}
      {problem && (
        <div className="mem-row-problem">
          {limits ? memoryWordsProblemText(problem, limits, strings) : strings.needsTitle}
        </div>
      )}
    </>
  )
}

interface RowProps {
  memory: PortalMemory
  strings: Messages['memories']
  limits: PortalMemoryLimits | undefined
  savedOn: string
  busy: boolean
  pending: AskedAct | null
  onAsk: (kind: AskedAct | null) => void
  onUse: () => void
  onStopUsing: () => void
  onDiscard: (reason: string) => void
  onReword: (text: string, title: string | undefined) => void
}

function MemoryRow({
  memory,
  strings,
  limits,
  savedOn,
  busy,
  pending,
  onAsk,
  onUse,
  onStopUsing,
  onDiscard,
  onReword
}: RowProps) {
  const [reason, setReason] = useState('')
  const [text, setText] = useState(memory.text)
  const [title, setTitle] = useState(memory.title ?? '')
  const titleChange = titleToSend(memory, title)
  const changed = text.trim() !== memory.text.trim() || titleChange !== undefined

  return (
    <li className={`mem-row${memory.inForce ? '' : ' is-waiting'}`}>
      <MemoryText memory={memory} limits={limits} strings={strings} />
      <div className="mem-row-meta">
        <KindChip kind={memory.kind} kinds={strings.kinds} />
        <Badge kind={memory.inForce ? 'success' : 'waiting'} dot={!memory.inForce}>
          {memory.inForce ? strings.inUse : strings.waiting}
        </Badge>
        <span className="muted">{savedOn}</span>
      </div>

      {pending === null && (
        <div className="mem-row-acts">
          {rowActs(memory).map((act) =>
            actsAtOnce(act) ? (
              <button key={act} type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={onUse}>
                {busy ? <Spinner size={13} /> : <Icon name="check" size={13} />} {strings.use}
              </button>
            ) : (
              <button
                key={act}
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => onAsk(act)}
              >
                {act === 'reword' ? strings.reword : act === 'stop' ? strings.stopUsing : strings.discard}
              </button>
            )
          )}
        </div>
      )}

      {pending === 'stop' && (
        <div className="mem-row-form">
          <div className="mem-row-hint">{strings.stopUsingHint}</div>
          <div className="mem-row-acts">
            <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={onStopUsing}>
              {busy ? strings.working : strings.stopUsing}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onAsk(null)}>
              {strings.cancel}
            </button>
          </div>
        </div>
      )}

      {pending === 'discard' && (
        <div className="mem-row-form">
          <label className="field-label" htmlFor={`why-${memory.id}`}>
            {strings.discardReasonLabel}
          </label>
          <input
            id={`why-${memory.id}`}
            className="input"
            value={reason}
            placeholder={strings.discardReasonPlaceholder}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="mem-row-hint muted">{strings.discardHint}</div>
          <div className="mem-row-acts">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || reason.trim().length === 0}
              onClick={() => onDiscard(reason.trim())}
            >
              {busy ? strings.working : strings.confirm}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onAsk(null)}>
              {strings.cancel}
            </button>
          </div>
        </div>
      )}

      {pending === 'reword' && (
        <div className="mem-row-form">
          <MemoryWordsFields
            idPrefix={`reword-${memory.id}`}
            title={title}
            onTitle={setTitle}
            text={text}
            onText={setText}
            limits={limits}
            strings={strings}
          />
          <div className="mem-row-hint muted">{strings.rewordHint}</div>
          <div className="mem-row-acts">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || !changed || memoryWordsProblem(text, title, limits) !== null}
              onClick={() => onReword(text.trim(), titleChange)}
            >
              {busy ? strings.working : strings.save}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onAsk(null)}>
              {strings.cancel}
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

/**
 * Writing one by hand.
 *
 * Collapsed until asked for, because most visits are to read or to decide, and
 * a permanently open box on every Smith would turn a list into a form. The hint
 * says the two things somebody needs before they type: that nobody else sees it,
 * and that it takes effect on their next run.
 *
 * What somebody is typing belongs to the OPEN box, so the box is what holds it:
 * the form below is rendered only while it is open, and closing it — by saving,
 * by cancelling, by opening another Smith's — takes the draft with it. Keeping
 * the fields alive across a close left the sentence that had just been stored
 * sitting in the box the next time it was opened, one click from being stored
 * twice.
 */
function WriteMemory({
  flowId,
  strings,
  limits,
  open,
  busy,
  onOpen,
  onSave
}: {
  flowId: string
  strings: Messages['memories']
  limits: PortalMemoryLimits | undefined
  open: boolean
  busy: boolean
  onOpen: () => void
  onSave: (text: string, kind: PortalMemoryKindChoice, title: string) => void
}) {
  if (!open) {
    return (
      <button type="button" className="btn btn-ghost btn-sm mem-write-open" onClick={onOpen}>
        <Icon name="plus" size={13} /> {strings.write}
      </button>
    )
  }
  return (
    <WriteMemoryDraft flowId={flowId} strings={strings} limits={limits} busy={busy} onClose={onOpen} onSave={onSave} />
  )
}

/** The open box itself. Mounted while it is open and only while it is open. */
function WriteMemoryDraft({
  flowId,
  strings,
  limits,
  busy,
  onClose,
  onSave
}: {
  flowId: string
  strings: Messages['memories']
  limits: PortalMemoryLimits | undefined
  busy: boolean
  onClose: () => void
  onSave: (text: string, kind: PortalMemoryKindChoice, title: string) => void
}) {
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState<PortalMemoryKindChoice>('preference')
  return (
    <div className="mem-write">
      <label className="field-label" htmlFor={`write-kind-${flowId}`}>
        {strings.writeKindLabel}
      </label>
      <select
        id={`write-kind-${flowId}`}
        className="select"
        value={kind}
        onChange={(e) => setKind(e.target.value as PortalMemoryKindChoice)}
      >
        {WRITE_KINDS.map((k) => (
          <option key={k} value={k}>
            {strings.writeKinds[k]?.label ?? k}
          </option>
        ))}
      </select>
      {/* The example is what makes the choice legible: the two abstractions read
          alike until somebody sees a sentence of each. */}
      <div className="mem-row-hint muted">{strings.writeExample(strings.writeKinds[kind]?.example ?? '')}</div>
      <MemoryWordsFields
        idPrefix={`write-${flowId}`}
        title={title}
        onTitle={setTitle}
        text={text}
        onText={setText}
        placeholder={strings.writePlaceholder}
        limits={limits}
        strings={strings}
      />
      <div className="mem-row-hint muted">{strings.writeHint}</div>
      <div className="mem-row-acts">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={busy || memoryWordsProblem(text, title, limits) !== null}
          onClick={() => onSave(text.trim(), kind, typedTitle(title))}
        >
          {busy ? strings.working : strings.writeSave}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onClose}>
          {strings.cancel}
        </button>
      </div>
    </div>
  )
}
